import { Action } from '@ngrx/store';

import {
    TwoFactorChallenge,
    TwoFactorEmailStartResponse,
    TwoFactorFinalizePayload,
    TwoFactorTranslationKey,
    UserModel,
} from '../../models';

export enum AuthActionTypes {
    CHECK_AUTH = '[Auth] CheckAuth',
    CHECK_AUTH_ERROR = '[Auth] CheckAuthError',
    // SIGNUP_USER = '[Auth] SignupUser',
    LOGIN_USER = '[Auth] LoginUser',
    LOGIN_USER_SUCCESS = '[Auth] LoginUserSuccess',
    LOGIN_USER_ERROR = '[Auth] LoginUserError',
    LOGIN_USER_ERROR_CLEAR = '[Auth] LoginUserErrorClear',
    LOGIN_USER_TWO_FACTOR_REQUIRED = '[Auth] LoginUserTwoFactorRequired',
    START_TWO_FACTOR_EMAIL_CODE = '[Auth] StartTwoFactorEmailCode',
    START_TWO_FACTOR_EMAIL_CODE_SUCCESS = '[Auth] StartTwoFactorEmailCodeSuccess',
    RESEND_TWO_FACTOR_EMAIL_CODE = '[Auth] ResendTwoFactorEmailCode',
    VERIFY_TWO_FACTOR_EMAIL_CODE = '[Auth] VerifyTwoFactorEmailCode',
    START_TWO_FACTOR_ID_GOV = '[Auth] StartTwoFactorIdGov',
    RESET_TWO_FACTOR_SUBMITTING = '[Auth] ResetTwoFactorSubmitting',
    COMPLETE_TWO_FACTOR_ID_GOV = '[Auth] CompleteTwoFactorIdGov',
    CANCEL_TWO_FACTOR = '[Auth] CancelTwoFactor',
    TWO_FACTOR_ERROR = '[Auth] TwoFactorError',
    LOGOUT_USER = '[Auth] LogoutUser',
    SET_AUTH_SESSION_MESSAGE = '[Auth] SetAuthSessionMessage',
}

export interface LoginUserPayload {
    email: string;
    password: string;
    terminatePreviousSession?: boolean;
}

export type LogoutReason = 'manual' | 'inactivity' | 'unauthorized';
export type AuthSessionMessageParams = Record<string, string | number | boolean>;
export type AuthSessionMessageKey =
    | 'sessionLifecycle.inactivityLocked'
    | 'sessionLifecycle.terminatedByNewLogin'
    | 'sessionLifecycle.terminatedByAdmin'
    | 'sessionLifecycle.expired'
    | 'sessionLifecycle.loggedOut'
    | 'sessionLifecycle.genericUnauthorized'
    | 'sessionLifecycle.multisessionCancelMessage'
    | 'auth.twoFactor.messages.cancelled'
    | 'auth.twoFactor.messages.timeout'
    | 'auth.twoFactor.errors.pendingExpired'
    | 'auth.twoFactor.errors.technical';

export interface StartTwoFactorIdGovPayload {
    pendingToken: string;
    returnUrl: string;
    deepLink: string;
    expiresAt?: number;
}

export interface CancelTwoFactorPayload {
    messageKey?: AuthSessionMessageKey;
    messageParams?: AuthSessionMessageParams;
}

export interface TwoFactorErrorPayload {
    messageKey: TwoFactorTranslationKey;
    clearChallenge?: boolean;
}

export interface LogoutUserPayload {
    reason: LogoutReason;
    messageKey?: AuthSessionMessageKey;
    messageParams?: AuthSessionMessageParams;
    fromSync?: boolean;
}

export class CheckAuth implements Action {
    readonly type = AuthActionTypes.CHECK_AUTH;
}

export class CheckAuthError implements Action {
    readonly type = AuthActionTypes.CHECK_AUTH_ERROR;

    constructor(public payload: Error) {}
}

export class LoginUser implements Action {
    readonly type = AuthActionTypes.LOGIN_USER;
    constructor(public payload: LoginUserPayload) {}
}

export class LoginUserSuccess implements Action {
    readonly type = AuthActionTypes.LOGIN_USER_SUCCESS;

    constructor(public payload: UserModel) {}
}

export class LoginUserError implements Action {
    readonly type = AuthActionTypes.LOGIN_USER_ERROR;

    constructor(public payload: any) {} // ??
}

export class LoginUserErrorClear implements Action {
    readonly type = AuthActionTypes.LOGIN_USER_ERROR_CLEAR;

    constructor(public clearSessionMessage = false) {}
}

export class LoginUserTwoFactorRequired implements Action {
    readonly type = AuthActionTypes.LOGIN_USER_TWO_FACTOR_REQUIRED;

    constructor(public payload: TwoFactorChallenge) {}
}

export class StartTwoFactorEmailCode implements Action {
    readonly type = AuthActionTypes.START_TWO_FACTOR_EMAIL_CODE;

    constructor(public pendingToken: string) {}
}

export class StartTwoFactorEmailCodeSuccess implements Action {
    readonly type = AuthActionTypes.START_TWO_FACTOR_EMAIL_CODE_SUCCESS;

    constructor(
        public payload: TwoFactorEmailStartResponse,
        public resendAvailableAt: number,
    ) {}
}

export class ResendTwoFactorEmailCode implements Action {
    readonly type = AuthActionTypes.RESEND_TWO_FACTOR_EMAIL_CODE;

    constructor(public pendingToken: string) {}
}

export class VerifyTwoFactorEmailCode implements Action {
    readonly type = AuthActionTypes.VERIFY_TWO_FACTOR_EMAIL_CODE;

    constructor(public payload: TwoFactorFinalizePayload) {}
}

export class StartTwoFactorIdGov implements Action {
    readonly type = AuthActionTypes.START_TWO_FACTOR_ID_GOV;

    constructor(public payload: StartTwoFactorIdGovPayload) {}
}

export class ResetTwoFactorSubmitting implements Action {
    readonly type = AuthActionTypes.RESET_TWO_FACTOR_SUBMITTING;
}

export class CompleteTwoFactorIdGov implements Action {
    readonly type = AuthActionTypes.COMPLETE_TWO_FACTOR_ID_GOV;

    constructor(public payload: TwoFactorFinalizePayload) {}
}

export class CancelTwoFactor implements Action {
    readonly type = AuthActionTypes.CANCEL_TWO_FACTOR;

    constructor(public payload: CancelTwoFactorPayload = {}) {}
}

export class TwoFactorError implements Action {
    readonly type = AuthActionTypes.TWO_FACTOR_ERROR;

    constructor(public payload: TwoFactorErrorPayload) {}
}

export class LogoutUser implements Action {
    readonly type = AuthActionTypes.LOGOUT_USER;

    constructor(public payload: LogoutUserPayload = { reason: 'manual' }) {}
}

export class SetAuthSessionMessage implements Action {
    readonly type = AuthActionTypes.SET_AUTH_SESSION_MESSAGE;

    constructor(
        public payload: AuthSessionMessageKey,
        public params?: AuthSessionMessageParams,
    ) {}
}

export type AuthActions =
    | CheckAuth
    | CheckAuthError
    | LoginUser
    | LoginUserSuccess
    | LoginUserError
    | LoginUserErrorClear
    | LoginUserTwoFactorRequired
    | StartTwoFactorEmailCode
    | StartTwoFactorEmailCodeSuccess
    | ResendTwoFactorEmailCode
    | VerifyTwoFactorEmailCode
    | StartTwoFactorIdGov
    | ResetTwoFactorSubmitting
    | CompleteTwoFactorIdGov
    | CancelTwoFactor
    | TwoFactorError
    | LogoutUser
    | SetAuthSessionMessage;
